import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import {
  FacebookShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
} from "react-share";
import SvgFaceBookIcon from "./share-icons/SvgFaceBookIcon";
import SvgTwiterIcone from "./share-icons/SvgTwiterIcone";
import SvgTelegramIcon from "./share-icons/SvgTelegramIcon";
import SvgWhatsAppIcon from "./share-icons/SvgWhatsAppIcon";
import SvgInstagramIcon from "./share-icons/SvgInstagramIcon";
import SvgSnapchatIcon from "./share-icons/SvgSnapchatIcon";
import SvgLinkedInIcon from "./share-icons/SvgLinkedInIcon";
import Separator from "@/app/components/lib/Separator";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslation } from "@/hooks/localization/useTranslations";
import useIsClient from "@/hooks/useIsClient";
import { useState } from "react";
import AppToast from "@/app/components/lib/toast/AppToast";

export enum ShareIconTypes {
  product = "product",
  blog = "blog",
}

interface props {
  startLabel?: string;
  type: ShareIconTypes;
}

const customBtnStyle: React.CSSProperties = {
  backgroundColor: "transparent",
  border: "none",
  padding: 0,
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
};

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback to execCommand below
  }
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand("copy");
    textArea.remove();
    return successful;
  } catch (err) {
    console.error("Failed to copy link:", err);
    return false;
  }
};

const SnapchatShareBtn = ({ url, children }: { url: string; children: React.ReactNode }) => {
  const handleClick = () => {
    window.open(`https://www.snapchat.com/share?link=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
  };
  return (
    <button onClick={handleClick} style={customBtnStyle} type="button">
      {children}
    </button>
  );
};

const InstagramShareBtn = ({
  url,
  onCopied,
  children,
}: {
  url: string;
  onCopied?: () => void;
  children: React.ReactNode;
}) => {
  const handleClick = async () => {
    const isMobileDevice =
      typeof navigator !== "undefined" &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    if (isMobileDevice && typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ url });
        return;
      } catch (err: any) {
        if (err?.name === "AbortError") {
          return;
        }
      }
    }

    const copied = await copyToClipboard(url);
    if (copied && onCopied) {
      onCopied();
    }
  };

  return (
    <button onClick={handleClick} style={customBtnStyle} type="button">
      {children}
    </button>
  );
};

const SocialShareBtns: React.FC<props> = ({ startLabel, type }) => {
  const mobile = useIsMobile();
  const t = useTranslation();
  const isClient = useIsClient();
  const [showToast, setShowToast] = useState(false);
  const getCompany = useGetCompanyQuery();
  const enableShareIcons =
    getCompany?.data?.result.theme.components.product
      .enable_product_share_icons;
  if (!enableShareIcons) return null;
  const shareUrl = isClient ? window?.location?.href : "";

  const shareIcons = [
    { Btn: TwitterShareButton, Icon: SvgTwiterIcone },
    { Btn: FacebookShareButton, Icon: SvgFaceBookIcon },
    { Btn: TelegramShareButton, Icon: SvgTelegramIcon },
    { Btn: WhatsappShareButton, Icon: SvgWhatsAppIcon },
    { Btn: LinkedinShareButton, Icon: SvgLinkedInIcon },
    { Btn: SnapchatShareBtn, Icon: SvgSnapchatIcon },
    { Btn: InstagramShareBtn, Icon: SvgInstagramIcon, onCopied: () => setShowToast(true) },
  ];
  return (
    <>
      <AppToast
        show={showToast}
        setShow={setShowToast}
        message={<div>{t.link_copied_successfully}</div>}
        success
      />
      <div
        className="product-share-btns"
        style={type === ShareIconTypes.blog ? { margin: 0 } : {}}
      >
        <Separator
          secondary={mobile}
          startLabel={startLabel ?? t.share_product}
        />
        <div className="share-btns">
          {shareIcons.map(({ Btn, Icon, onCopied }, index) => {
            const Component = Btn as React.ComponentType<any>;
            return (
              <Component key={index} url={shareUrl} onCopied={onCopied}>
                <div className="icon-container">
                  <Icon />
                </div>
              </Component>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default SocialShareBtns;
